import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import LoadingOverlay from "./LoadingOverlay";
import PrintQCShipment from "./PrintQCShipment";
import { fetchFormNos } from "../utils/formNos";
import { FORM_KEYS } from "../config/formIds";

function QCShipmentList() {
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [cancelGroupId, setCancelGroupId] = useState(null);
  const [formNos, setFormNos] = useState({});
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchPending();
    fetchFormNos().then(setFormNos);
  }, []);

  const fetchPending = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/transaction/qc_pending_stockin",
      );
      setRecords(res.data.result || []);
    } catch {
      toast.error("Failed to fetch pending shipments.");
    } finally {
      setIsLoading(false);
    }
  };

  const grouped = records.reduce((acc, item) => {
    const gid = item.transaction_group_id || "UNGROUPED";
    if (!acc[gid]) acc[gid] = [];
    acc[gid].push(item);
    return acc;
  }, {});

  const sortedGroups = Object.entries(grouped).sort(([, a], [, b]) => {
    const latest = (arr) =>
      Math.max(...arr.map((x) => new Date(x.transaction_time)));
    return latest(b) - latest(a);
  });

  const handleReprint = (groupId, groupItems) => {
    const first = groupItems[0];
    setPrintData({
      formId: first.form_id,
      groupId,
      submittedDate: first.transaction_time,
      items: groupItems.map((item) => ({
        product_code: item.product_code,
        product_name: item.product_name,
        ship_qty: item.stock_in,
        remark: item.transaction_remark || "",
        customer: item.transaction_for || "",
        username: item.transaction_by,
      })),
    });
  };

  const startEdit = (item) => {
    setEditingId(item.transaction_id);
    setEditValues({
      stock_in: item.stock_in,
      transaction_remark: item.transaction_remark || "",
      transaction_for: item.transaction_for || "",
    });
  };

  const handleSaveEdit = async (item) => {
    if (!editValues.stock_in || Number(editValues.stock_in) <= 0) {
      toast.warning("Qty must be greater than 0.");
      return;
    }
    try {
      await axios.patch(
        "https://eps.elkomprintama.com/patch/api/store/transaction/update",
        {
          transactionId: item.transaction_id,
          stockIn: Number(editValues.stock_in),
          stockOut: 0,
          transactionType: item.transaction_type,
          transactionBy: item.transaction_by,
          transactionFor: editValues.transaction_for || null,
          transactionRemark: editValues.transaction_remark || null,
        },
      );
      toast.success("Updated!");
      setEditingId(null);
      fetchPending();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to update.");
    }
  };

  const handleCancelGroup = async (groupId) => {
    try {
      await axios.put(
        "https://eps.elkomprintama.com/api/store/stocks/cancel_stockin",
        {
          groupId,
          approvedBy: user?.username || null,
        },
      );
      toast.success("Shipment cancelled.");
      setCancelGroupId(null);
      fetchPending();
    } catch (err) {
      toast.error(err?.response?.data?.msg || "Failed to cancel.");
    }
  };

  function stripedRow(i) {
    return i % 2 === 0 ? "bg-gray-50" : "bg-white";
  }

  return (
    <>
      <div className="w-full rounded-lg bg-teal-600 mb-4">
        <label className="block uppercase text-white mb-2 tracking-wide text-5xl text-center font-bold">
          QC Shipment List
        </label>
      </div>

      <div className="bg-gray-100 shadow-md px-5 py-5 rounded-lg relative">
        {isLoading && <LoadingOverlay message="Loading..." />}

        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-500">
            Showing all{" "}
            <span className="font-semibold text-teal-700">
              Pending Stock In
            </span>{" "}
            shipments awaiting store approval. Click 🖨️ on any group to re-print
            its QR form.
          </p>
          <button
            className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold px-4 py-2 rounded"
            onClick={fetchPending}
          >
            Refresh
          </button>
        </div>

        {sortedGroups.length === 0 ? (
          <div className="text-center py-12 text-gray-400 italic text-sm">
            No outstanding QC shipments
          </div>
        ) : (
          <div className="space-y-4">
            {sortedGroups.map(([groupId, groupItems]) => {
              const first = groupItems[0];
              const totalQty = groupItems.reduce(
                (s, x) => s + (x.stock_in || 0),
                0,
              );
              const submittedTime = format(
                new Date(first.transaction_time),
                "dd-MM-yyyy HH:mm",
              );

              return (
                <div
                  key={groupId}
                  className="border rounded-lg overflow-hidden shadow-sm"
                >
                  <div className="bg-teal-600 text-white px-4 py-2 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-bold uppercase tracking-wide">
                        Form #{first.form_id || "—"}
                      </span>
                      <span className="text-xs opacity-80">
                        Submitted by:{" "}
                        <span className="font-semibold">
                          {first.transaction_by}
                        </span>
                      </span>
                      <span className="text-xs opacity-80">
                        {submittedTime}
                      </span>
                      <span className="bg-teal-800 text-white text-xs px-2 py-0.5 rounded-full font-bold">
                        {groupItems.length} item
                        {groupItems.length > 1 ? "s" : ""} | Total: {totalQty}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className="text-xs opacity-70 font-mono truncate max-w-[200px]"
                        title={groupId}
                      >
                        {groupId.substring(0, 18)}…
                      </span>
                      <button
                        className="bg-white text-teal-700 hover:bg-teal-50 text-xs font-bold px-3 py-1 rounded flex items-center gap-1"
                        onClick={() => handleReprint(groupId, groupItems)}
                      >
                        🖨️ Re-print
                      </button>
                      <button
                        className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-3 py-1 rounded"
                        onClick={() => setCancelGroupId(groupId)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  <table className="text-xs w-full border-collapse">
                    <thead className="bg-gray-700 text-white uppercase text-left">
                      <tr>
                        <th className="px-4 py-2 border-r">No</th>
                        <th className="px-4 py-2 border-r">Product Code</th>
                        <th className="px-4 py-2 border-r">Product Name</th>
                        <th className="px-4 py-2 border-r">Group</th>
                        <th className="px-4 py-2 border-r">Qty (In)</th>
                        <th className="px-4 py-2 border-r">Remark</th>
                        <th className="px-4 py-2 border-r">Customer / For</th>
                        <th className="px-4 py-2 border-r">Input By</th>
                        <th className="px-4 py-2 text-center">Edit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupItems.map((item, i) => {
                        const isEditing = editingId === item.transaction_id;
                        return (
                          <tr
                            key={item.transaction_id}
                            className={`border-b hover:bg-teal-50 ${stripedRow(i)}`}
                          >
                            <td className="px-4 py-2 border-r text-gray-500">
                              {i + 1}
                            </td>
                            <td className="px-4 py-2 border-r font-mono font-semibold text-teal-700">
                              {item.product_code}
                            </td>
                            <td className="px-4 py-2 border-r">
                              {item.product_name}
                            </td>
                            <td className="px-4 py-2 border-r text-gray-500">
                              {item.group_name}
                            </td>
                            <td className="px-4 py-2 border-r font-bold">
                              {isEditing ? (
                                <input
                                  type="number"
                                  min="1"
                                  autoFocus
                                  className="border border-green-400 rounded px-2 py-0.5 w-20 text-center focus:outline-none"
                                  value={editValues.stock_in}
                                  onChange={(e) =>
                                    setEditValues((v) => ({
                                      ...v,
                                      stock_in: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                item.stock_in
                              )}
                            </td>
                            <td className="px-4 py-2 border-r text-gray-600">
                              {isEditing ? (
                                <input
                                  name="remark" type="text"
                                  className="border border-gray-300 rounded px-2 py-0.5 w-full focus:outline-none"
                                  value={editValues.transaction_remark}
                                  onChange={(e) =>
                                    setEditValues((v) => ({
                                      ...v,
                                      transaction_remark: e.target.value,
                                    }))
                                  }
                                  placeholder="Remark"
                                />
                              ) : (
                                item.transaction_remark
                              )}
                            </td>
                            <td className="px-4 py-2 border-r text-gray-600">
                              {isEditing ? (
                                <input
                                  name="customer_for" type="text"
                                  className="border border-gray-300 rounded px-2 py-0.5 w-full focus:outline-none"
                                  value={editValues.transaction_for}
                                  onChange={(e) =>
                                    setEditValues((v) => ({
                                      ...v,
                                      transaction_for: e.target.value,
                                    }))
                                  }
                                  placeholder="Customer / For"
                                />
                              ) : (
                                item.transaction_for
                              )}
                            </td>
                            <td className="px-4 py-2 border-r text-gray-600">
                              {item.transaction_by}
                            </td>
                            <td className="px-4 py-2 text-center whitespace-nowrap">
                              {isEditing ? (
                                <div className="flex gap-1 justify-center">
                                  <button
                                    className="bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-2 py-1 rounded"
                                    onClick={() => handleSaveEdit(item)}
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-medium px-2 py-1 rounded"
                                    onClick={() => setEditingId(null)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <button
                                  className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded"
                                  onClick={() => startEdit(item)}
                                >
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {cancelGroupId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-[360px] p-6">
            <h3 className="text-sm font-bold text-gray-800 mb-2">
              Cancel Shipment?
            </h3>
            <p className="text-xs text-gray-500 mb-5">
              This will cancel all items in this shipment group. The transaction
              will be marked as
              <span className="font-semibold text-red-600"> CANCELLED</span> and
              cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold py-2.5 rounded"
                onClick={() => handleCancelGroup(cancelGroupId)}
              >
                Yes, Cancel Shipment
              </button>
              <button
                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold py-2.5 rounded"
                onClick={() => setCancelGroupId(null)}
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {printData && (
        <PrintQCShipment
          formId={printData.formId}
          groupId={printData.groupId}
          items={printData.items}
          submittedDate={printData.submittedDate}
          onDone={() => setPrintData(null)}
          formNumber={formNos[FORM_KEYS.QC_TRANSFER]}
        />
      )}
    </>
  );
}

export default QCShipmentList;
